import { useState } from "react";
import type { ChangeEvent } from "react";
import { OWN_PAGE_KINDS, OWN_PAGE_LIMITS, editsCatalog, otherSize, ownPagesOf } from "@speisekarte/core";
import type { MenuOwnPages, OwnPage, OwnPageKind } from "@speisekarte/core";
import { uploadOwnPage } from "../own-pages.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";

const mb = (bytes: number) => `${(bytes / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
const mm = (pt: number) => Math.round((pt / 72) * 25.4);

/** The paper by its size: „A4“, „A5“, else the size in mm. */
export function paperName(p: Pick<OwnPage, "width" | "height">): string {
  const [w, h] = [mm(p.width), mm(p.height)].sort((a, b) => a - b) as [number, number];
  const named = [["A3", 297, 420], ["A4", 210, 297], ["A5", 148, 210], ["A6", 105, 148], ["DIN lang", 99, 210]] as const;
  const hit = named.find(([, a, b]) => Math.abs(a - w) <= 2 && Math.abs(b - h) <= 2);
  return hit ? `${hit[0]}${p.width > p.height ? " quer" : ""}` : `${mm(p.width)} × ${mm(p.height)} mm`;
}

/**
 * „Eigene Seiten“ (S50): the house's finished pages as PDF (a title page from the designer, its
 * story, a page with its links), each a Vorspann or a Nachsatz. A menu takes them in step 3; they go
 * into print, PDF and Word unchanged, never on the net. In the catalogues, beside the texts: the
 * other pieces the menus are made of.
 */
export function OwnPages() {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const pages = data.ownPages ?? [];
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<OwnPageKind>("front");
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const used = (id: string) => data.menus.filter((m) => m.ownPages?.front.includes(id) || m.ownPages?.back.includes(id)).length;

  function choose(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    e.target.value = "";
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.pdf$/i, "").slice(0, OWN_PAGE_LIMITS.name));
    setState(f && f.size > OWN_PAGE_LIMITS.bytes ? { error: `${mb(f.size)}: höchstens ${mb(OWN_PAGE_LIMITS.bytes)}.` } : {});
  }

  async function upload() {
    if (!file) return;
    setState({ busy: true });
    try {
      const page = await uploadOwnPage(file, name.trim(), kind);
      setFile(null);
      setName("");
      setState({ ok: `„${page.name}“ ist da (${page.pages === 1 ? "1 Seite" : `${page.pages} Seiten`}, ${paperName(page)}). In Schritt 3 einer Speisekarte wählst du, ob sie davor oder dahinter kommt.` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  async function change(page: OwnPage, body: { name?: string; kind?: OwnPageKind }) {
    try {
      await store.change(() => api(`/api/own-pages/${page.id}`, { method: "PATCH", body }));
      setState({});
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  async function remove(page: OwnPage) {
    const n = used(page.id);
    if (n > 0 && !confirm(`„${page.name}“ steht bei ${n === 1 ? "einer Speisekarte" : `${n} Speisekarten`} davor oder dahinter. Trotzdem löschen? Die Speisekarten kommen dann ohne sie.`)) return;
    try {
      await store.change(() => api(`/api/own-pages/${page.id}`, { method: "DELETE" }));
      setState({});
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <div className="import">
      <p>
        <a className="back" href="#/kataloge">← Kataloge</a>
      </p>
      <section className="card">
        <h2>Eigene PDF-Seiten</h2>
        <p className="hint">Fertige Seiten als PDF, etwa die Titelseite vom Grafiker, die Geschichte des Hauses oder eine Seite mit Website und Instagram. Ein <b>Vorspann</b> kommt vor die Speisekarte, ein <b>Nachsatz</b> dahinter, im Druck, im PDF und in Word, genau so, wie sie sind. Im Netz erscheinen sie nicht. Welche Speisekarte welche bekommt, wählst du in Schritt 3.</p>
        {pages.length === 0 ? (
          <p className="muted">Noch keine eigenen Seiten.</p>
        ) : (
          <ul className="list own-pages" data-testid="own-pages">
            {pages.map((p) => (
              <li key={p.id} className="own-page" data-testid="own-page">
                <input defaultValue={p.name} key={p.name} maxLength={OWN_PAGE_LIMITS.name} readOnly={!canEdit} aria-label="Name der Seite" onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== p.name && void change(p, { name: e.target.value })} data-testid="own-page-name" />
                <select value={p.kind} disabled={!canEdit} onChange={(e) => void change(p, { kind: e.target.value as OwnPageKind })} aria-label="Vorspann oder Nachsatz" data-testid="own-page-kind">
                  {(Object.keys(OWN_PAGE_KINDS) as OwnPageKind[]).map((k) => (
                    <option key={k} value={k}>
                      {OWN_PAGE_KINDS[k]}
                    </option>
                  ))}
                </select>
                <span className="muted small" title={mb(p.bytes)}>
                  {p.pages === 1 ? "1 Seite" : `${p.pages} Seiten`}, {paperName(p)}
                </span>
                <span className="muted small count" title="Bei so vielen Speisekarten">
                  {used(p.id) || ""}
                </span>
                {canEdit ? (
                  <button className="link danger" onClick={() => void remove(p)} aria-label={`${p.name} löschen`} title="Löschen">
                    ✕
                  </button>
                ) : (
                  <span />
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <>
            <h3 style={{ marginTop: "1.2rem" }}>Seite hinzufügen</h3>
            {pages.length >= OWN_PAGE_LIMITS.perBusiness ? (
              <p className="muted">Höchstens {OWN_PAGE_LIMITS.perBusiness} eigene Seiten; bitte erst eine löschen.</p>
            ) : (
              <>
                <label className="upload">
                  <span className="button">{file ? "Andere PDF-Datei …" : "PDF-Datei wählen …"}</span>
                  <input type="file" accept="application/pdf,.pdf" onChange={choose} hidden data-testid="own-page-file" />
                </label>
                {file && (
                  <p className="small">
                    {file.name} <span className="muted">{mb(file.size)}</span>
                  </p>
                )}
                <label>
                  Name
                  <input value={name} onChange={(e) => [setName(e.target.value), setState({})]} maxLength={OWN_PAGE_LIMITS.name} placeholder="z. B. Titelseite" data-testid="own-page-new-name" />
                </label>
                <label>
                  Art
                  <select value={kind} onChange={(e) => setKind(e.target.value as OwnPageKind)} data-testid="own-page-new-kind">
                    <option value="front">Vorspann (vor der Speisekarte)</option>
                    <option value="back">Nachsatz (hinter der Speisekarte)</option>
                  </select>
                </label>
                <div className="buttons">
                  <button className="primary" disabled={!file || !name.trim() || file.size > OWN_PAGE_LIMITS.bytes || state.busy} onClick={() => void upload()} data-testid="own-page-upload">
                    {state.busy ? "Lädt hoch …" : "Hochladen"}
                  </button>
                </div>
                <p className="muted small">
                  PDF bis {mb(OWN_PAGE_LIMITS.bytes)} und {OWN_PAGE_LIMITS.pages} Seiten, ohne Passwort. Passt das Papier nicht zur Speisekarte (A4 vor A5), wird die Seite eingepasst.
                </p>
              </>
            )}
          </>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && (
          <p className="ok-text" data-testid="own-page-ok">
            {state.ok}
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * In step 3 of a menu (S50): which own pages come before and after it. Only once the house has one
 * (S38); never for the insert of the recommendations (S37), which stays one sheet.
 */
export function OwnPagesChoice({ menu, data, paper, insert, canEdit, onChange }: { menu: { ownPages?: MenuOwnPages }; data: Data; paper: { width: number; height: number }; insert: boolean; canEdit: boolean; onChange: (next: MenuOwnPages | undefined) => void }) {
  const all = data.ownPages ?? [];
  if (all.length === 0) return null;
  if (insert) return null;
  const chosen = menu.ownPages ?? { front: [], back: [] };
  const toggle = (side: keyof MenuOwnPages, id: string, on: boolean) => {
    const next = { ...chosen, [side]: on ? [...chosen[side].filter((x) => x !== id), id] : chosen[side].filter((x) => x !== id) };
    // In the order of the collection, as the list shows them.
    for (const k of ["front", "back"] as const) next[k] = all.map((p) => p.id).filter((x) => next[k].includes(x));
    onChange(next.front.length || next.back.length ? next : undefined);
  };
  const { front, back } = ownPagesOf(menu, all);
  const fitted = [...front, ...back].filter((p) => otherSize(p, paper));
  const side = (key: keyof MenuOwnPages, label: string) => {
    const list = all.filter((p) => p.kind === key || chosen[key].includes(p.id));
    return list.length ? (
      <div className="own-side">
        <span className="small muted">{label}</span>
        {list.map((p) => (
          <label key={p.id} className="check">
            <input type="checkbox" checked={chosen[key].includes(p.id)} disabled={!canEdit} onChange={(e) => toggle(key, p.id, e.target.checked)} data-testid={`own-${key}-${p.id}`} />
            <span>
              {p.name} <span className="muted small">({p.pages === 1 ? "1 Seite" : `${p.pages} Seiten`}, {paperName(p)})</span>
            </span>
          </label>
        ))}
      </div>
    ) : null;
  };
  return (
    <div className="card out-web" data-testid="own-pages-choice">
      <h3>Eigene PDF-Seiten</h3>
      <p className="hint">
        Kommen in Druck, PDF und Word vor oder hinter diese Speisekarte, ohne Seitenzahl; in Word als Bild, dort nicht bearbeitbar; im Netz nicht. Neue unter „Kataloge“ → <a href="#/eigene-seiten">„Eigene PDF-Seiten“</a>.
      </p>
      {side("front", "Davor (Vorspann)")}
      {side("back", "Dahinter (Nachsatz)")}
      {fitted.length > 0 && (
        <p className="notice small" data-testid="own-pages-fitted">
          {fitted.map((p) => `„${p.name}“ (${paperName(p)})`).join(", ")} {fitted.length === 1 ? "hat" : "haben"} ein anderes Papier als die Speisekarte ({paperName(paper)}) und {fitted.length === 1 ? "wird" : "werden"} eingepasst.
        </p>
      )}
    </div>
  );
}
